'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addSupporterPaymentMethod,
  removeSupporterPaymentMethod,
  saveSupporterPaymentMethod,
} from '@sola/proxy-sdk/supporters.js';
import {
  PaymentMethodTypesEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import {
  GraphQLSupporterAddress,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { encryptCreditCardData } from '@sola/shared-utils/credit-cards.js';

import { MUTATION_KEYS, QUERY_KEYS } from '../constants';
import { paymentMethodsQuery, supporterAddressQuery } from '../queries';
import { getSupporterAddresses } from '../utils/addresses';
import {
  useReadyForQueries,
  useSupporterMutation,
  useSupporterQuery,
} from './base';

/**
 * Hook to add a new payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodAdd = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useSupporterMutation<
    GraphQLSupporterPaymentMethod | undefined,
    Error,
    SupporterPaymentMethodDetails
  >({
    mutationFn: (paymentMethod) => {
      const paymentMethodDetails = { ...paymentMethod };

      if (paymentMethodDetails.type === PaymentMethodTypesEnum.Card) {
        const { cardNumber, cvv } = paymentMethodDetails;
        const { creditCardEncrypted, cvvEncrypted } = encryptCreditCardData(
          { cvv, creditCard: cardNumber },
          !!cvv,
          window.TxEncrypt,
          process.env.NEXT_PUBLIC_TOKENEX_ENCRYPTION_KEY as string,
        );

        paymentMethodDetails.cardNumber = creditCardEncrypted ?? '';
        paymentMethodDetails.cvv = cvvEncrypted ?? '';
      }

      return addSupporterPaymentMethod(
        globalId as string,
        paymentMethodDetails,
      );
    },
    onSuccess: (paymentMethod?: GraphQLSupporterPaymentMethod) => {
      if (!paymentMethod?.id) {
        // TODO: Should we throw an error here?
        return;
      }

      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (paymentMethods: GraphQLSupporterPaymentMethod[]) => {
          return paymentMethods
            ? [...paymentMethods, paymentMethod]
            : [paymentMethod];
        },
      );
    },
  });
};

/**
 * Hook to update a payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodUpdate = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useMutation<
    GraphQLSupporterPaymentMethod,
    Error,
    GraphQLSupporterPaymentMethod
  >({
    mutationFn: (paymentMethod) =>
      saveSupporterPaymentMethod(globalId as string, paymentMethod),
    onSuccess: (updatedPaymentMethod: GraphQLSupporterPaymentMethod) => {
      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (currentPaymentMethods: GraphQLSupporterPaymentMethod[]) => {
          const newPaymentMethods = [...currentPaymentMethods];
          const paymentMethodIndex = currentPaymentMethods?.findIndex(
            (p) => p.id === updatedPaymentMethod.id,
          );
          newPaymentMethods[paymentMethodIndex] = updatedPaymentMethod;
          return newPaymentMethods;
        },
      );
    },
  });
};

/**
 * Hook to remove a payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodRemove = (paymentMethodIdKey?: string) => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();
  return useMutation({
    mutationKey: [MUTATION_KEYS.paymentMethodRemove, paymentMethodIdKey],
    mutationFn: () => {
      return removeSupporterPaymentMethod(globalId, paymentMethodIdKey);
    },
    onSuccess: async (paymentMethodId) => {
      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (paymentMethods: GraphQLSupporterPaymentMethod[]) => {
          return paymentMethods && paymentMethodId
            ? paymentMethods?.filter(
                (paymentMethod) => paymentMethod?.id !== paymentMethodId,
              )
            : [];
        },
      );
    },
  });
};

export const useSupporterBillingAddresses = () => {
  const { data, isLoading, isFetching, isPending, isError } = useSupporterQuery(
    [supporterAddressQuery, paymentMethodsQuery],
  );

  // @ts-expect-error  TODO We need to tighten up useSupporterQuery so these types are inferred
  const [supporterAddress, supporterPaymentMethods]: [
    GraphQLSupporterAddress | undefined,
    GraphQLSupporterPaymentMethod[],
  ] = data;

  return {
    supporterAddresses: supporterAddress
      ? getSupporterAddresses(supporterAddress, supporterPaymentMethods)
      : undefined,
    isLoading,
    isFetching,
    isPending,
    isError,
  };
};
